import { sampleSkyAuthoredNodes, sampleSkyAuthoredPose } from './115-sky-authored-pose.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';

const prepared = new WeakMap();
const clamp = (n) => Math.max(0, Math.min(1, n));
const ease = (n) => {
  const t = clamp(n);
  return t * t * (3 - 2 * t);
};
function multiply(a, b) {
  const [x, y, z, w] = a,
    [i, j, k, r] = b;
  return [
    w * i + x * r + y * k - z * j,
    w * j - x * k + y * r + z * i,
    w * k + x * j - y * i + z * r,
    w * r - x * i - y * j - z * k,
  ];
}
const turn = (q, axis, angle) =>
  multiply(q, [
    axis[0] * Math.sin(angle / 2),
    axis[1] * Math.sin(angle / 2),
    axis[2] * Math.sin(angle / 2),
    Math.cos(angle / 2),
  ]);
function source(rig) {
  if (prepared.has(rig)) return prepared.get(rig);
  const nodes = sampleSkyAuthoredNodes(rig, 'Idle', 0),
    body = rig.nodes.findIndex((n) => n.name === 'cinderheart_root'),
    head = rig.nodes.findIndex((n) => n.name === 'head_joint'),
    neck = rig.nodes.findIndex((n) => n.name === 'neck_joint');
  const support = [-1, 1].map((side) =>
    Array.from({ length: 26 }, (_, i) => {
      const quaternion = turn(nodes[body].quaternion, [0, 1, 0], (side * i * 2 * Math.PI) / 180);
      const override = {
        node: body,
        ...nodes[body],
        quaternion,
        position: [...nodes[body].position],
      };
      const pose = sampleSkyAuthoredPose(rig, 'CoolantLock', 0, null, { overrides: [override] });
      return -pose.bounds.min[1] + 0.12;
    }),
  );
  const value = { nodes, body, head, neck, support };
  prepared.set(rig, value);
  return value;
}
/** Prepare exact source-surface support samples once per cached rig. No extra
 * skin evaluation occurs during combat: support lookup is26 scalar samples per
 * side, while116 evaluates the one authoritative rendered/contact pose. */
export function prepareSkyCinderPose(rig) {
  source(rig);
}

/** Produce bounded, shared numeric bone transforms. A maximum50-degree side
 * collapse replaces inversion. Original rigid lower-leg parts buckle/separate
 * at their existing joints; no substitute mesh or invisible collider is made.
 * Permanent injury survives reservoir refill and changes subsequent stance. */
export function skyCinderPose(boss) {
  const s = boss.cinderheart,
    rig = boss.authoredRig,
    art = source(rig),
    now = boss.age,
    side = s.lockedSide || 1;
  const collapse =
    s.phase === 'rolling'
      ? ease((s.age - 0.8) / 3.2)
      : s.phase === 'exposed'
        ? 1
        : s.phase === 'righting'
          ? 1 - ease(s.age / 5)
          : 0;
  const left = s.broken.slice(0, 2).filter(Boolean).length,
    right = s.broken.slice(2).filter(Boolean).length;
  const resting = (left - right) * 2.5,
    angle = resting * (1 - collapse) + side * 50 * collapse;
  const index = Math.abs(angle) / 2,
    lower = Math.floor(index),
    table = art.support[angle < 0 ? 0 : 1];
  const y =
    table[lower] +
    ((table[Math.min(25, lower + 1)] ?? table[lower]) - table[lower]) * (index - lower);
  const body = { node: art.body, ...structuredClone(art.nodes[art.body]) };
  body.quaternion = turn(body.quaternion, [0, 1, 0], (angle * Math.PI) / 180);
  // Correct the previous authoritative surface minimum, without another skin
  // evaluation. Smooth fracture motion makes this a bounded one-tick support
  // correction; it affects the shared root joint, never rendering alone.
  if (s.supportReady) {
    const floor = boss.authoredPose.bounds.min[1] - boss.rootTransform.position[1];
    s.supportLift = Math.max(-2, Math.min(2, (s.supportLift ?? 0) + 0.12 - floor));
  }
  s.supportReady = true;
  body.position[1] += y + (s.supportLift ?? 0);
  const overrides = [body];
  for (const index of [art.neck, art.head]) {
    const node = { node: index, ...structuredClone(art.nodes[index]) };
    const weight = index === art.neck ? 1 : 0.65;
    node.quaternion = turn(
      node.quaternion,
      [1, 0, 0],
      weight * (0.1 * Math.sin(now * 1.1) - collapse * 0.18),
    );
    node.quaternion = turn(node.quaternion, [0, 0, 1], weight * 0.1 * Math.sin(now * 0.65));
    overrides.push(node);
  }
  for (let limb = 0; limb < 4; limb++) {
    const damaged = s.broken[limb],
      fracture = damaged ? ease((now - s.fracturedAt[limb]) / 0.9) : 0;
    for (let link = 0; link < 3; link++) {
      const i = rig.nodes.findIndex((n) => n.name === `leg_0${limb}_${link}`);
      if (i < 0) continue;
      const locked = s.lockedJoints.find((n) => n.node === i);
      if (!locked && !damaged) continue;
      const node = { node: i, ...structuredClone(locked ?? art.nodes[i]) };
      if (damaged) {
        // The actual distal fragments pull apart at their existing mechanical
        // seams and fold inward; their geometry and source UVs remain intact.
        node.quaternion = turn(
          node.quaternion,
          [0, 1, 0],
          (limb < 2 ? -1 : 1) * fracture * (link === 0 ? 0.08 : link === 1 ? 0.32 : -0.24),
        );
        if (link > 0) node.position[2] += 0.35 * fracture;
      }
      overrides.push(node);
    }
  }
  s.collapseAngle = angle;
  s.frost = s.frozen.map((f, i) => (f ? ease((now - s.frozenAt[i]) / 0.9) : 0));
  s.fracture = s.broken.map((b, i) => (b ? ease((now - s.fracturedAt[i]) / 0.9) : 0));
  return overrides;
}

/** Record one grounded load transfer after each earned, supported side collapse.
 * Called after116 evaluates the authoritative source skin. A settled angle alone
 * is insufficient: an actual low source vertex must meet the terrain within the
 * existing support clearance. Units are metres and the boss's active seconds.
 * No collision, damage, pose, pilot state or independent presentation clock changes.
 */
export function recordSkyCinderSupportImpact(state, boss) {
  const s = boss.cinderheart,
    pose = boss.authoredPose;
  if (
    boss.worldId !== 'cinderheart' ||
    !s ||
    boss.defeated ||
    !['rolling', 'exposed'].includes(s.phase) ||
    !(s.rolls > 0) ||
    s.groundImpact?.sequence === s.rolls ||
    !s.lockedSide ||
    !s.broken.some(Boolean) ||
    Math.abs(s.collapseAngle ?? 0) < 49.5 ||
    !pose ||
    !Number.isFinite(boss.age)
  )
    return false;
  const floor = boss.rootTransform.position[1],
    minimum = pose.bounds.min[1];
  if (minimum - floor < -0.25 || minimum - floor > 0.35) return false;
  let contact = null,
    distance = 0.35;
  for (let meshIndex = 0; meshIndex < pose.meshes.length; meshIndex++) {
    const vertices = pose.meshes[meshIndex].vertices;
    for (let i = 0; i < vertices.length; i += 3) {
      if (vertices[i + 1] > minimum + 0.35) continue;
      const point = { x: vertices[i], y: vertices[i + 1], z: vertices[i + 2] };
      const groundY = skyTerrainHeight(point.x, point.z, state.terrainTravel ?? 0);
      const clearance = point.y - groundY;
      if (Math.abs(clearance) > distance) continue;
      distance = Math.abs(clearance);
      contact = { point, groundY, clearance, meshIndex, vertexIndex: i / 3 };
    }
  }
  if (!contact) return false;
  s.groundImpact = {
    ...contact,
    kind: 'supported-collapse',
    sequence: s.rolls,
    at: boss.age,
    side: s.lockedSide < 0 ? -1 : 1,
  };
  return true;
}
